import type { FilterSchema } from '@/shared/components/filter/validation'
import type { WidgetPeriods } from '../types'

import React from 'react'
import { addDays, format } from 'date-fns'
import { any, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useBIRevenueAnalyticQuery } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'

import { Skeleton } from '@/shared/components/ui/skeleton'
import { getDifferenceInPercents } from '@/shared/lib/calculation/getDifferenceInPercents'
import { cn } from '@/shared/lib/utils'
import { DateFormat } from '@/shared/tools/dates'
import { formatMoney, NumberFormats } from '@/shared/tools/locale'

import PercentDifferenceBadge from '../../PercentDifferenceBadge'
import { Widget, WidgetContent, WidgetHeader } from '../../Widget'

export default function NumericRevenueUnderManagementWidget({
	className,
	commonPeriods,
	commonFilters,
}: {
	className?: string
	commonPeriods: WidgetPeriods
	commonFilters: FilterSchema[]
}) {
	const { t } = useTranslation()

	const start_date = format(
		addDays(new Date(commonPeriods.main.end_date), -14),
		DateFormat.system
	)
	const end_date = commonPeriods.main.end_date
	const currency = useSelectedCurrency()
	const biCommonQuery = {
		currency,
		start_date,
		end_date,
		revenue_start_date: start_date,
		revenue_end_date: end_date,
		label: 'NumericRevenueUnderManagement',
		metrics: ['revenue'],
		date_aggregation: null,
		dimensions: [],
		skip_dates: true,
	}
	const biQueryConfig = { select: prop('data.0.0') }
	const biComparedPeriodQuery = useBIRevenueAnalyticQuery<number>(
		{
			...biCommonQuery,
			filters: [
				{ name: 'optimization_lag', operation: 'eq', value: 2 },
				{ name: 'is_repricing_today', operation: 'bl', value: true },
				{ name: 'price_change_status', operation: 'is_not', value: 'locked' },
				...commonFilters,
			],
		},
		biQueryConfig
	)
	const biMainPeriodQuery = useBIRevenueAnalyticQuery<number>(
		{
			...biCommonQuery,
			filters: [
				{ name: 'optimization_lag', operation: 'eq', value: 1 },
				{ name: 'is_repricing_today', operation: 'bl', value: true },
				{ name: 'price_change_status', operation: 'is_not', value: 'locked' },
				...commonFilters,
			],
		},
		biQueryConfig
	)
	const isLoading = any(prop('isFetching'), [
		biMainPeriodQuery,
		biComparedPeriodQuery,
	])
	const diff = getDifferenceInPercents(
		biMainPeriodQuery.data!,
		biComparedPeriodQuery.data!
	)

	return (
		<Widget className={cn('relative flex flex-auto flex-col', className)}>
			<WidgetHeader
				className='pb-0'
				title={t('widget.num_revenue_under_management.title')}
				tooltip={t('widget.revenue_under_management.tooltip')}
			/>
			<WidgetContent className='truncate'>
				{isLoading ? (
					<Skeleton className='col-span-2 mt-auto h-10 w-32' />
				) : (
					<div className='flex flex-col space-y-1'>
						{Number.isFinite(diff) ? (
							<div className='flex items-center space-x-2'>
								<PercentDifferenceBadge value={diff} />
								<span className='truncate text-xs text-muted-foreground'>
									({t('past')}:{' '}
									{formatMoney(
										biComparedPeriodQuery.data!,
										currency,
										NumberFormats.Compact
									)}
									)
								</span>
							</div>
						) : null}
						<span className='truncate text-3xl font-semibold'>
							{formatMoney(
								biMainPeriodQuery.data!,
								currency,
								NumberFormats.Compact
							)}
						</span>
					</div>
				)}
			</WidgetContent>
		</Widget>
	)
}
